// TaskViewSidebar.js
import { useState, useEffect } from "react";
import { Button, Form, Badge, Row, Col, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getTaskById,
  updateTask,
  createSubTask,
  getSubTasksByTask,
  updateSubTask,
} from "../Api/TaskMaster";
import "./TaskViewSidebar.css";
import { getUsersByIds } from "../Api/Auth";
import { getTeamById } from "../Api/TeamMaster";
import { fetchProjectDetails } from "../Api/ViewProjects";

const TaskViewSidebar = ({ show, onHide, taskId, onTaskUpdated }) => {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalTask, setOriginalTask] = useState(null);
  const [showSubTaskModal, setShowSubTaskModal] = useState(false);
  const [subTaskLoading, setSubTaskLoading] = useState(false);
  const [subTasks, setSubTasks] = useState([]);

  const [editingSubTask, setEditingSubTask] = useState(null);
  const [showEditSubTaskModal, setShowEditSubTaskModal] = useState(false);

  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberDetails, setTeamMemberDetails] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = String(userDetails?.userId);
  const orgId = userDetails?.organisationId;
  const username = userDetails?.username;

  // SubTask form state
  const [subTaskForm, setSubTaskForm] = useState({
    SubTaskName: "",
    SubTaskDescription: "",
    SubTaskAssignedTo: "",
    SubTaskStatus: "Pending",
    SubTaskDueDate: "",
    SubTaskActualStateDate: "",
    SubTaskActualEndDate: "",
    SubTaskCreatedBy: "",
  });

  // Fetch team members when project is available
  const fetchTeamMembers = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingTeam(true);
      // First get project details to get the team ID
      const projectData = await fetchProjectDetails(projectId);
      if (projectData && projectData.teamAssigned) {
        const teamData = await getTeamById(projectData.teamAssigned);
        if (teamData && teamData.teamMembers) {
          setTeamMembers(teamData.teamMembers);
          await fetchUserDetails(teamData.teamMembers);
        } else {
          setTeamMembers([]);
          setTeamMemberDetails([]);
        }
      } else {
        setTeamMembers([]);
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      setTeamMemberDetails([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Fetch user details for team members
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setTeamMemberDetails([]);
      return;
    }
    try {
      const numericUserIds = userIds
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));

      if (numericUserIds.length === 0) {
        setTeamMemberDetails([]);
        return;
      }

      const response = await getUsersByIds(numericUserIds);
      if (response && response.users) {
        setTeamMemberDetails(response.users);
      } else {
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setTeamMemberDetails([]);
    }
  };

  const getUserDisplayName = (memberId) => {
    if (!memberId) return "Unassigned";

    const userDetail = teamMemberDetails.find(
      (user) => user.userId === parseInt(memberId)
    );

    if (userDetail) {
      return `${userDetail.username} (${userDetail.email})`;
    }

    return memberId; // Fallback to ID if user not found
  };

  useEffect(() => {
    if (show && taskId) {
      loadTaskDetails();
      loadSubTasks();
    } else {
      // Reset state when sidebar closes
      setTask(null);
      setOriginalTask(null);
      setIsEditing(false);
      setSubTasks([]);
      setTeamMembers([]);
      setTeamMemberDetails([]);
    }
  }, [show, taskId]);

  const loadTaskDetails = async () => {
    setLoading(true);
    try {
      const taskData = await getTaskById(taskId);
      setTask(taskData);
      setOriginalTask(taskData);

      // Fetch team members for the project
      if (taskData.projectBelong) {
        await fetchTeamMembers(taskData.projectBelong);
      }
    } catch (error) {
      console.error("Error loading task details:", error);
      toast.error("Failed to load task details");
    } finally {
      setLoading(false);
    }
  };

  const loadSubTasks = async () => {
    try {
      const subtasksData = await getSubTasksByTask(taskId);
      setSubTasks(subtasksData);
    } catch (error) {
      console.error("Error loading subtasks:", error);
    }
  };

  const handleFieldChange = (field, value) => {
    if (!isEditing) setIsEditing(true);
    setTask((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!task) return;

    setSaving(true);
    try {
      const updateData = {
        taskName: task.taskName,
        taskDescription: task.taskDescription,
        assignedTo: task.assignedTo,
        taskPriority: task.taskPriority,
        taskStatus: task.taskStatus,
        taskStartDate: task.taskStartDate,
        taskEndDate: task.taskEndDate,
        taskTags: task.taskTags,
        comments: task.comments,
      };

      await updateTask(taskId, updateData);
      setOriginalTask(task);
      setIsEditing(false);
      toast.success("Task updated successfully");

      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTask(originalTask);
    setIsEditing(false);
  };

  const handleClose = () => {
    if (isEditing) {
      // Ask for confirmation if there are unsaved changes
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?"
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  // SubTask handlers
  const handleAddSubTask = () => {
    // Reset form and set default values
    setSubTaskForm({
      SubTaskName: "",
      SubTaskDescription: "",
      SubTaskAssignedTo: task?.assignedTo || "",
      SubTaskStatus: "Pending",
      SubTaskDueDate: task?.taskEndDate
        ? moment(task.taskEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualStateDate: "",
      SubTaskActualEndDate: "",
      SubTaskCreatedBy: userId,
    });
    setShowSubTaskModal(true);
  };

  const handleSubTaskFormChange = (field, value) => {
    setSubTaskForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubTaskSubmit = async () => {
    if (!subTaskForm.SubTaskName.trim()) {
      toast.error("SubTask name is required");
      return;
    }

    setSubTaskLoading(true);
    try {
      const subTaskData = {
        ...subTaskForm,
        SubTaskBelongTask: taskId, // Link to parent task
        SubTaskCreatedBy: userId,
      };

      await createSubTask(subTaskData, username);
      toast.success("SubTask created successfully");
      setShowSubTaskModal(false);

      // Reload subtasks
      await loadSubTasks();

      // Notify parent component
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error("Failed to create subtask");
    } finally {
      setSubTaskLoading(false);
    }
  };

  // Handler to open edit subtask modal
  const handleEditSubTask = (subTask) => {
    setEditingSubTask(subTask);
    setSubTaskForm({
      SubTaskName: subTask.subTaskName || "",
      SubTaskDescription: subTask.subTaskDescription || "",
      SubTaskAssignedTo: subTask.subTaskAssignedTo || "",
      SubTaskStatus: subTask.subTaskStatus || "Pending",
      SubTaskDueDate: subTask.subTaskDueDate
        ? moment(subTask.subTaskDueDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualStateDate: subTask.subTaskActualStateDate
        ? moment(subTask.subTaskActualStateDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualEndDate: subTask.subTaskActualEndDate
        ? moment(subTask.subTaskActualEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskCreatedBy: userId,
    });
    setShowEditSubTaskModal(true);
  };

  // Handler to update existing subtask
  const handleUpdateSubTask = async () => {
    if (!editingSubTask || !subTaskForm.SubTaskName.trim()) {
      toast.error("SubTask name is required");
      return;
    }

    setSubTaskLoading(true);
    try {
      const updateData = {
        SubTaskName: subTaskForm.SubTaskName,
        SubTaskDescription: subTaskForm.SubTaskDescription,
        SubTaskAssignedTo: subTaskForm.SubTaskAssignedTo,
        SubTaskStatus: subTaskForm.SubTaskStatus,
        SubTaskDueDate: subTaskForm.SubTaskDueDate || null,
        SubTaskActualStateDate: subTaskForm.SubTaskActualStateDate || null,
        SubTaskActualEndDate: subTaskForm.SubTaskActualEndDate || null,
        SubTaskEditedBy: userId,
      };

      await updateSubTask(editingSubTask.subTaskId, updateData);
      toast.success("SubTask updated successfully");
      setShowEditSubTaskModal(false);
      setEditingSubTask(null);

      // Reload subtasks
      await loadSubTasks();

      // Notify parent component
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error("Error updating subtask:", error);
      toast.error("Failed to update subtask");
    } finally {
      setSubTaskLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Active: "status-active",
      Completed: "status-completed",
      Pending: "status-pending",
      "In Progress": "status-in-progress",
    };
    return statusMap[status] || "status-pending";
  };

  const getPriorityBadge = (priority) => {
    const priorityMap = {
      High: "priority-high",
      Medium: "priority-medium",
      Low: "priority-low",
    };
    return priorityMap[priority] || "priority-medium";
  };

  if (!show) return null;

  return (
    <>
      <div className="task-sidebar-overlay" onClick={handleClose}></div>
      <div className="task-view-sidebar">
        {/* Header with Close Button */}
        <div className="task-sidebar-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 task-sidebar-title">Task Details</h5>
            <Button
              variant="link"
              className="task-close-btn"
              onClick={handleClose}
              title="Close sidebar"
            ></Button>
          </div>

          {/* Add SubTask Button */}
          <div className="d-flex justify-content-between align-items-center">
            {isEditing && (
              <div className="edit-controls">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="me-2"
                >
                  <i className="fas fa-save me-1"></i>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <i className="fas fa-times me-1"></i>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="task-sidebar-content">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading task details...</span>
              </div>
              <p className="mt-2 text-muted">Loading task details...</p>
            </div>
          ) : task ? (
            <div className="task-details">
              {/* Task Name */}
              <div className="task-field-group">
                <label className="task-field-label">Task Name</label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    value={task.taskName || ""}
                    onChange={(e) =>
                      handleFieldChange("taskName", e.target.value)
                    }
                    className="task-field-input"
                    placeholder="Enter task name"
                    style={{ fontSize: "10px" }}
                  />
                ) : (
                  <div
                    className="task-field-value editable-field"
                    onClick={() => setIsEditing(true)}
                    title="Click to edit"
                  >
                    <p style={{ fontSize: "15px", margin: "0", padding: "0" }}>
                      {task.taskName}
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="task-field-group">
                <label className="task-field-label">Description</label>
                {isEditing ? (
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={task.taskDescription || ""}
                    onChange={(e) =>
                      handleFieldChange("taskDescription", e.target.value)
                    }
                    className="task-field-input"
                    placeholder="Enter task description"
                  />
                ) : (
                  <div
                    className="task-field-value editable-field task-description-text"
                    onClick={() => setIsEditing(true)}
                    title="Click to edit"
                  >
                    {task.taskDescription || (
                      <span className="text-muted">
                        No description provided
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Row className="g-3">
                {/* Status */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">Status</label>
                    {isEditing ? (
                      <Form.Select
                        value={task.taskStatus || ""}
                        onChange={(e) =>
                          handleFieldChange("taskStatus", e.target.value)
                        }
                        className="task-field-input"
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="On Hold">On Hold</option>
                      </Form.Select>
                    ) : (
                      <div className="task-field-value">
                        <span
                          className={`task-badge ${getStatusBadge(
                            task.taskStatus
                          )}`}
                        >
                          {task.taskStatus}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Priority */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">Priority</label>
                    {isEditing ? (
                      <Form.Select
                        value={task.taskPriority || ""}
                        onChange={(e) =>
                          handleFieldChange("taskPriority", e.target.value)
                        }
                        className="task-field-input"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                      </Form.Select>
                    ) : (
                      <div className="task-field-value">
                        <span
                          className={`task-badge ${getPriorityBadge(
                            task.taskPriority
                          )}`}
                        >
                          {task.taskPriority}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                {/* Assigned To */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-user me-2"></i>
                      Assigned To
                    </label>
                    {isEditing ? (
                      loadingTeam ? (
                        <Form.Control
                          type="text"
                          disabled
                          value="Loading team members..."
                          className="bg-light"
                        />
                      ) : (
                        <Form.Select
                          name="assignedTo"
                          value={task.assignedTo || ""}
                          onChange={(e) =>
                            handleFieldChange("assignedTo", e.target.value)
                          }
                          className="task-field-input"
                        >
                          <option value="">Select Team Member</option>
                          {teamMembers.map((memberId) => {
                            const displayName = getUserDisplayName(memberId);
                            return (
                              <option key={memberId} value={memberId}>
                                {displayName}
                              </option>
                            );
                          })}
                        </Form.Select>
                      )
                    ) : (
                      <div
                        className="task-field-value editable-field"
                        onClick={() => setIsEditing(true)}
                        title="Click to edit"
                        style={{ fontSize: "13px" }}
                      >
                        {getUserDisplayName(task.assignedTo) || (
                          <span className="text-muted">Unassigned</span>
                        )}
                      </div>
                    )}
                    {teamMembers.length === 0 && !loadingTeam && (
                      <Form.Text
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        No team members available for this project
                      </Form.Text>
                    )}
                  </div>
                </Col>

                {/* Task ID */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-hashtag me-2"></i>
                      Task ID
                    </label>
                    <div
                      className="task-field-value task-id-value"
                      style={{ fontSize: "13px" }}
                    >
                      {task.taskId}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Rest of your existing code remains the same... */}
              <Row className="g-3">
                {/* Start Date */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-play-circle me-2"></i>
                      Start Date
                    </label>
                    {isEditing ? (
                      <Form.Control
                        type="date"
                        value={
                          task.taskStartDate
                            ? moment(task.taskStartDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange("taskStartDate", e.target.value)
                        }
                        className="task-field-input"
                      />
                    ) : (
                      <div
                        className="task-field-value editable-field"
                        onClick={() => setIsEditing(true)}
                        title="Click to edit"
                        style={{ fontSize: "13px" }}
                      >
                        {task.taskStartDate ? (
                          <>
                            <i className="fas fa-calendar me-1"></i>
                            {moment(task.taskStartDate).format("MMM DD, YYYY")}
                          </>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>

                {/* End Date */}
                <Col sm={6}>
                  <div className="task-field-group">
                    <label className="task-field-label">
                      <i className="fas fa-flag-checkered me-2"></i>
                      Due Date
                    </label>
                    {isEditing ? (
                      <Form.Control
                        type="date"
                        value={
                          task.taskEndDate
                            ? moment(task.taskEndDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          handleFieldChange("taskEndDate", e.target.value)
                        }
                        className="task-field-input"
                      />
                    ) : (
                      <div
                        className="task-field-value editable-field"
                        onClick={() => setIsEditing(true)}
                        title="Click to edit"
                        style={{ fontSize: "13px" }}
                      >
                        {task.taskEndDate ? (
                          <>
                            <i className="fas fa-calendar me-1"></i>
                            {moment(task.taskEndDate).format("MMM DD, YYYY")}
                          </>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Tags */}
              <div className="task-field-group">
                <label className="task-field-label">
                  <i className="fas fa-tags me-2"></i>
                  Tags
                </label>
                {isEditing ? (
                  <Form.Control
                    type="text"
                    value={task.taskTags || ""}
                    onChange={(e) =>
                      handleFieldChange("taskTags", e.target.value)
                    }
                    className="task-field-input"
                    placeholder="Comma separated tags (e.g., design, development, urgent)"
                  />
                ) : (
                  <div
                    className="task-field-value editable-field"
                    onClick={() => setIsEditing(true)}
                    title="Click to edit"
                  >
                    {task.taskTags ? (
                      <div className="task-tags">
                        {task.taskTags.split(",").map((tag, index) => (
                          <Badge
                            key={index}
                            bg="light"
                            text="dark"
                            className="me-1 mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            {tag.trim()}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted" style={{ fontSize: "10px" }}>
                        No tags
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* SubTasks Section */}
              <div className="task-field-group">
                <label
                  className="task-field-label"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  Sub Tasks ({subTasks.length})
                  <span>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddSubTask}
                      className="me-2"
                    >
                      Add Sub Task
                    </Button>
                  </span>
                </label>
                <div className="subtasks-list">
                  {subTasks.length > 0 ? (
                    subTasks.map((subTask) => (
                      <div key={subTask.subTaskId} className="subtask-item">
                        <div className="subtask-header">
                          <span className="subtask-name">
                            {subTask.subTaskName}
                          </span>
                          <div className="subtask-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditSubTask(subTask)}
                              className="me-1"
                              title="Edit subtask"
                              style={{ padding: "3px 10px", margin: "10px" }}
                            >
                              Edit
                            </Button>
                            <span
                              className={`subtask-status ${getStatusBadge(
                                subTask.subTaskStatus
                              )}`}
                            >
                              {subTask.subTaskStatus}
                            </span>
                          </div>
                        </div>
                        {subTask.subTaskDescription && (
                          <div className="subtask-description">
                            {subTask.subTaskDescription}
                          </div>
                        )}
                        <div className="subtask-meta">
                          <small className="text-muted">
                            Due:{" "}
                            {subTask.subTaskDueDate
                              ? moment(subTask.subTaskDueDate).format(
                                  "MMM DD, YYYY"
                                )
                              : "Not set"}
                          </small>
                          {subTask.subTaskAssignedTo && (
                            <small className="text-muted ms-2">
                              Assigned to:{" "}
                              {getUserDisplayName(subTask.subTaskAssignedTo)}
                            </small>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted text-center py-2">
                      No sub tasks yet. Click "Add Sub Task" to create one.
                    </div>
                  )}
                </div>
              </div>

              {/* Created Info */}
              <div className="task-field-group">
                <div className="task-meta-info">
                  <small className="text-muted">
                    <i className="fas fa-clock me-1"></i>
                    Created{" "}
                    {task.createdDate
                      ? moment(task.createdDate).format(
                          "MMM DD, YYYY [at] h:mm A"
                        )
                      : "N/A"}
                    {task.createdBy && ` by ${task.createdBy}`}
                  </small>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-exclamation-triangle fa-2x mb-3"></i>
              <p>Task not found or unable to load task details.</p>
              <Button variant="outline-secondary" onClick={handleClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* SubTask Modal - Update the Assigned To field here too */}
      <Modal
        show={showSubTaskModal}
        onHide={() => setShowSubTaskModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-plus-circle me-2"></i>
            Add New Sub Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>SubTask Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={subTaskForm.SubTaskName}
                    onChange={(e) =>
                      handleSubTaskFormChange("SubTaskName", e.target.value)
                    }
                    placeholder="Enter subtask name"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={subTaskForm.SubTaskDescription}
                    onChange={(e) =>
                      handleSubTaskFormChange(
                        "SubTaskDescription",
                        e.target.value
                      )
                    }
                    placeholder="Enter subtask description"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Assigned To</Form.Label>
                  {loadingTeam ? (
                    <Form.Control
                      type="text"
                      disabled
                      value="Loading team members..."
                      className="bg-light"
                    />
                  ) : (
                    <Form.Select
                      value={subTaskForm.SubTaskAssignedTo || ""}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskAssignedTo",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Team Member</option>
                      {teamMembers.map((memberId) => {
                        const displayName = getUserDisplayName(memberId);
                        return (
                          <option key={memberId} value={memberId}>
                            {displayName}
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                  {teamMembers.length === 0 && !loadingTeam && (
                    <Form.Text
                      className="text-muted"
                      style={{ fontSize: "11px" }}
                    >
                      No team members available
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={subTaskForm.SubTaskStatus}
                    onChange={(e) =>
                      handleSubTaskFormChange("SubTaskStatus", e.target.value)
                    }
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subTaskForm.SubTaskDueDate}
                    onChange={(e) =>
                      handleSubTaskFormChange("SubTaskDueDate", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Actual Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subTaskForm.SubTaskActualStateDate}
                    onChange={(e) =>
                      handleSubTaskFormChange(
                        "SubTaskActualStateDate",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Actual End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subTaskForm.SubTaskActualEndDate}
                    onChange={(e) =>
                      handleSubTaskFormChange(
                        "SubTaskActualEndDate",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSubTaskModal(false)}
            disabled={subTaskLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubTaskSubmit}
            disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
          >
            {subTaskLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i>
                Create Sub Task
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit SubTask Modal - Update the Assigned To field here too */}
      <Modal
        show={showEditSubTaskModal}
        onHide={() => {
          setShowEditSubTaskModal(false);
          setEditingSubTask(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Edit Sub Task
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>SubTask Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={subTaskForm.SubTaskName}
                    onChange={(e) =>
                      handleSubTaskFormChange("SubTaskName", e.target.value)
                    }
                    placeholder="Enter subtask name"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={subTaskForm.SubTaskDescription}
                    onChange={(e) =>
                      handleSubTaskFormChange(
                        "SubTaskDescription",
                        e.target.value
                      )
                    }
                    placeholder="Enter subtask description"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Assigned To</Form.Label>
                  {loadingTeam ? (
                    <Form.Control
                      type="text"
                      disabled
                      value="Loading team members..."
                      className="bg-light"
                    />
                  ) : (
                    <Form.Select
                      value={subTaskForm.SubTaskAssignedTo || ""}
                      onChange={(e) =>
                        handleSubTaskFormChange(
                          "SubTaskAssignedTo",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Team Member</option>
                      {teamMembers.map((memberId) => {
                        const displayName = getUserDisplayName(memberId);
                        return (
                          <option key={memberId} value={memberId}>
                            {displayName}
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                  {teamMembers.length === 0 && !loadingTeam && (
                    <Form.Text
                      className="text-muted"
                      style={{ fontSize: "11px" }}
                    >
                      No team members available
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={subTaskForm.SubTaskStatus}
                    onChange={(e) =>
                      handleSubTaskFormChange("SubTaskStatus", e.target.value)
                    }
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subTaskForm.SubTaskDueDate}
                    onChange={(e) =>
                      handleSubTaskFormChange("SubTaskDueDate", e.target.value)
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Actual Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subTaskForm.SubTaskActualStateDate}
                    onChange={(e) =>
                      handleSubTaskFormChange(
                        "SubTaskActualStateDate",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Actual End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subTaskForm.SubTaskActualEndDate}
                    onChange={(e) =>
                      handleSubTaskFormChange(
                        "SubTaskActualEndDate",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditSubTaskModal(false);
              setEditingSubTask(null);
            }}
            disabled={subTaskLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateSubTask}
            disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
          >
            {subTaskLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i>
                Update Sub Task
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskViewSidebar;

// // TaskViewSidebar.js
// import { useState, useEffect } from "react";
// import { Button, Form, Badge, Row, Col, Modal } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";
// import {
//   getTaskById,
//   updateTask,
//   createSubTask,
//   getSubTasksByTask,
//   updateSubTask,
// } from "../Api/TaskMaster";
// import "./TaskViewSidebar.css";
// import { getUsersByIds } from "../Api/Auth"; // Adjust the path as needed

// const TaskViewSidebar = ({ show, onHide, taskId, onTaskUpdated }) => {
//   const [task, setTask] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [saving, setSaving] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [originalTask, setOriginalTask] = useState(null);
//   const [showSubTaskModal, setShowSubTaskModal] = useState(false);
//   const [subTaskLoading, setSubTaskLoading] = useState(false);
//   const [subTasks, setSubTasks] = useState([]);

//   const [editingSubTask, setEditingSubTask] = useState(null);
//   const [showEditSubTaskModal, setShowEditSubTaskModal] = useState(false);

//   const [teamMemberDetails, setTeamMemberDetails] = useState([]);

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = String(userDetails?.userId);
//   const orgId = userDetails?.organisationId;
//   const username = userDetails?.username;

//   // SubTask form state
//   const [subTaskForm, setSubTaskForm] = useState({
//     SubTaskName: "",
//     SubTaskDescription: "",
//     SubTaskAssignedTo: "",
//     SubTaskStatus: "Pending",
//     SubTaskDueDate: "",
//     SubTaskActualStateDate: "",
//     SubTaskActualEndDate: "",
//     SubTaskCreatedBy: "",
//   });

//   // Add this function after the userDetails declaration
//   const fetchUserDetails = async (userIds) => {
//     if (!userIds || userIds.length === 0) return;

//     try {
//       const numericUserIds = userIds
//         .map((id) => parseInt(id))
//         .filter((id) => !isNaN(id));

//       if (numericUserIds.length === 0) return;

//       const response = await getUsersByIds(numericUserIds);
//       if (response && response.users) {
//         setTeamMemberDetails(response.users);
//       }
//     } catch (error) {
//       console.error("Error fetching user details:", error);
//     }
//   };

//   const getUserDisplayName = (memberId) => {
//     if (!memberId) return "Unassigned";

//     const userDetail = teamMemberDetails.find(
//       (user) => user.userId === parseInt(memberId)
//     );

//     if (userDetail) {
//       return userDetail.username || userDetail.email || memberId;
//     }

//     return memberId; // Fallback to ID if user not found
//   };

//   useEffect(() => {
//     if (show && taskId) {
//       loadTaskDetails();
//       loadSubTasks();
//     } else {
//       // Reset state when sidebar closes
//       setTask(null);
//       setOriginalTask(null);
//       setIsEditing(false);
//       setSubTasks([]);
//       setTeamMemberDetails([]);
//     }
//   }, [show, taskId]);

//   const loadTaskDetails = async () => {
//     setLoading(true);
//     try {
//       const taskData = await getTaskById(taskId);
//       setTask(taskData);
//       setOriginalTask(taskData);

//       // Fetch user details for assigned users
//       if (taskData.assignedTo) {
//         await fetchUserDetails([taskData.assignedTo]);
//       }
//     } catch (error) {
//       console.error("Error loading task details:", error);
//       toast.error("Failed to load task details");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const loadSubTasks = async () => {
//     try {
//       const subtasksData = await getSubTasksByTask(taskId);
//       setSubTasks(subtasksData);
//     } catch (error) {
//       console.error("Error loading subtasks:", error);
//     }
//   };

//   const handleFieldChange = (field, value) => {
//     if (!isEditing) setIsEditing(true);
//     setTask((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const handleSave = async () => {
//     if (!task) return;

//     setSaving(true);
//     try {
//       const updateData = {
//         taskName: task.taskName,
//         taskDescription: task.taskDescription,
//         assignedTo: task.assignedTo,
//         taskPriority: task.taskPriority,
//         taskStatus: task.taskStatus,
//         taskStartDate: task.taskStartDate,
//         taskEndDate: task.taskEndDate,
//         taskTags: task.taskTags,
//         comments: task.comments,
//       };

//       await updateTask(taskId, updateData);
//       setOriginalTask(task);
//       setIsEditing(false);
//       toast.success("Task updated successfully");

//       if (onTaskUpdated) {
//         onTaskUpdated();
//       }
//     } catch (error) {
//       console.error("Error updating task:", error);
//       toast.error("Failed to update task");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const handleCancel = () => {
//     setTask(originalTask);
//     setIsEditing(false);
//   };

//   const handleClose = () => {
//     if (isEditing) {
//       // Ask for confirmation if there are unsaved changes
//       if (
//         window.confirm(
//           "You have unsaved changes. Are you sure you want to close?"
//         )
//       ) {
//         onHide();
//       }
//     } else {
//       onHide();
//     }
//   };

//   // SubTask handlers
//   const handleAddSubTask = () => {
//     // Reset form and set default values
//     setSubTaskForm({
//       SubTaskName: "",
//       SubTaskDescription: "",
//       SubTaskAssignedTo: task?.assignedTo || "",
//       SubTaskStatus: "Pending",
//       SubTaskDueDate: task?.taskEndDate
//         ? moment(task.taskEndDate).format("YYYY-MM-DD")
//         : "",
//       SubTaskActualStateDate: "",
//       SubTaskActualEndDate: "",
//       SubTaskCreatedBy: userId, // This should come from your auth context
//     });
//     setShowSubTaskModal(true);
//   };

//   const handleSubTaskFormChange = (field, value) => {
//     setSubTaskForm((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const handleSubTaskSubmit = async () => {
//     if (!subTaskForm.SubTaskName.trim()) {
//       toast.error("SubTask name is required");
//       return;
//     }

//     setSubTaskLoading(true);
//     try {
//       const subTaskData = {
//         ...subTaskForm,
//         SubTaskBelongTask: taskId, // Link to parent task
//         SubTaskCreatedBy: userId, // Replace with actual user ID from context
//       };

//       await createSubTask(subTaskData, username);
//       toast.success("SubTask created successfully");
//       setShowSubTaskModal(false);

//       // Reload subtasks
//       await loadSubTasks();

//       // Notify parent component
//       if (onTaskUpdated) {
//         onTaskUpdated();
//       }
//     } catch (error) {
//       console.error("Error creating subtask:", error);
//       toast.error("Failed to create subtask");
//     } finally {
//       setSubTaskLoading(false);
//     }
//   };

//   // Handler to open edit subtask modal
//   const handleEditSubTask = (subTask) => {
//     setEditingSubTask(subTask);
//     setSubTaskForm({
//       SubTaskName: subTask.subTaskName || "",
//       SubTaskDescription: subTask.subTaskDescription || "",
//       SubTaskAssignedTo: subTask.subTaskAssignedTo || "",
//       SubTaskStatus: subTask.subTaskStatus || "Pending",
//       SubTaskDueDate: subTask.subTaskDueDate
//         ? moment(subTask.subTaskDueDate).format("YYYY-MM-DD")
//         : "",
//       SubTaskActualStateDate: subTask.subTaskActualStateDate
//         ? moment(subTask.subTaskActualStateDate).format("YYYY-MM-DD")
//         : "",
//       SubTaskActualEndDate: subTask.subTaskActualEndDate
//         ? moment(subTask.subTaskActualEndDate).format("YYYY-MM-DD")
//         : "",
//       SubTaskCreatedBy: userId,
//     });
//     setShowEditSubTaskModal(true);
//   };

//   // Handler to update existing subtask
//   const handleUpdateSubTask = async () => {
//     if (!editingSubTask || !subTaskForm.SubTaskName.trim()) {
//       toast.error("SubTask name is required");
//       return;
//     }

//     setSubTaskLoading(true);
//     try {
//       const updateData = {
//         SubTaskName: subTaskForm.SubTaskName,
//         SubTaskDescription: subTaskForm.SubTaskDescription,
//         SubTaskAssignedTo: subTaskForm.SubTaskAssignedTo,
//         SubTaskStatus: subTaskForm.SubTaskStatus,
//         SubTaskDueDate: subTaskForm.SubTaskDueDate || null,
//         SubTaskActualStateDate: subTaskForm.SubTaskActualStateDate || null,
//         SubTaskActualEndDate: subTaskForm.SubTaskActualEndDate || null,
//         SubTaskEditedBy: userId, // Use EditedBy for updates
//       };

//       await updateSubTask(editingSubTask.subTaskId, updateData);
//       toast.success("SubTask updated successfully");
//       setShowEditSubTaskModal(false);
//       setEditingSubTask(null);

//       // Reload subtasks
//       await loadSubTasks();

//       // Notify parent component
//       if (onTaskUpdated) {
//         onTaskUpdated();
//       }
//     } catch (error) {
//       console.error("Error updating subtask:", error);
//       toast.error("Failed to update subtask");
//     } finally {
//       setSubTaskLoading(false);
//     }
//   };

//   const getStatusBadge = (status) => {
//     const statusMap = {
//       Active: "status-active",
//       Completed: "status-completed",
//       Pending: "status-pending",
//       "In Progress": "status-in-progress",
//     };
//     return statusMap[status] || "status-pending";
//   };

//   const getPriorityBadge = (priority) => {
//     const priorityMap = {
//       High: "priority-high",
//       Medium: "priority-medium",
//       Low: "priority-low",
//     };
//     return priorityMap[priority] || "priority-medium";
//   };

//   if (!show) return null;

//   return (
//     <>
//       <div className="task-sidebar-overlay" onClick={handleClose}></div>
//       <div className="task-view-sidebar">
//         {/* Header with Close Button */}
//         <div className="task-sidebar-header">
//           <div className="d-flex justify-content-between align-items-center">
//             <h5 className="mb-0 task-sidebar-title">Task Details</h5>
//             <Button
//               variant="link"
//               className="task-close-btn"
//               onClick={handleClose}
//               title="Close sidebar"
//             ></Button>
//           </div>

//           {/* Add SubTask Button */}
//           <div className="d-flex justify-content-between align-items-center">
//             {/* <Button
//               variant="primary"
//               size="sm"
//               onClick={handleAddSubTask}
//               className="me-2"
//             >
//               <i className="fas fa-plus-circle me-1"></i>
//               Add Sub Task
//             </Button> */}

//             {isEditing && (
//               <div className="edit-controls">
//                 <Button
//                   variant="success"
//                   size="sm"
//                   onClick={handleSave}
//                   disabled={saving}
//                   className="me-2"
//                 >
//                   <i className="fas fa-save me-1"></i>
//                   {saving ? "Saving..." : "Save Changes"}
//                 </Button>
//                 <Button
//                   variant="outline-secondary"
//                   size="sm"
//                   onClick={handleCancel}
//                   disabled={saving}
//                 >
//                   <i className="fas fa-times me-1"></i>
//                   Cancel
//                 </Button>
//               </div>
//             )}
//           </div>
//         </div>

//         <div className="task-sidebar-content">
//           {loading ? (
//             <div className="text-center py-4">
//               <div className="spinner-border text-primary" role="status">
//                 <span className="visually-hidden">Loading task details...</span>
//               </div>
//               <p className="mt-2 text-muted">Loading task details...</p>
//             </div>
//           ) : task ? (
//             <div className="task-details">
//               {/* Task Name */}
//               <div className="task-field-group">
//                 <label className="task-field-label">Task Name</label>
//                 {isEditing ? (
//                   <Form.Control
//                     type="text"
//                     value={task.taskName || ""}
//                     onChange={(e) =>
//                       handleFieldChange("taskName", e.target.value)
//                     }
//                     className="task-field-input"
//                     placeholder="Enter task name"
//                     style={{ fontSize: "10px" }}
//                   />
//                 ) : (
//                   <div
//                     className="task-field-value editable-field"
//                     onClick={() => setIsEditing(true)}
//                     title="Click to edit"
//                   >
//                     <p style={{ fontSize: "15px", margin: "0", padding: "0" }}>
//                       {task.taskName}
//                     </p>
//                   </div>
//                 )}
//               </div>

//               {/* Description */}
//               <div className="task-field-group">
//                 <label className="task-field-label">Description</label>
//                 {isEditing ? (
//                   <Form.Control
//                     as="textarea"
//                     rows={4}
//                     value={task.taskDescription || ""}
//                     onChange={(e) =>
//                       handleFieldChange("taskDescription", e.target.value)
//                     }
//                     className="task-field-input"
//                     placeholder="Enter task description"
//                   />
//                 ) : (
//                   <div
//                     className="task-field-value editable-field task-description-text"
//                     onClick={() => setIsEditing(true)}
//                     title="Click to edit"
//                   >
//                     {task.taskDescription || (
//                       <span className="text-muted">
//                         No description provided
//                       </span>
//                     )}
//                   </div>
//                 )}
//               </div>

//               <Row className="g-3">
//                 {/* Status */}
//                 <Col sm={6}>
//                   <div className="task-field-group">
//                     <label className="task-field-label">Status</label>
//                     {isEditing ? (
//                       <Form.Select
//                         value={task.taskStatus || ""}
//                         onChange={(e) =>
//                           handleFieldChange("taskStatus", e.target.value)
//                         }
//                         className="task-field-input"
//                       >
//                         <option value="Pending">Pending</option>
//                         <option value="In Progress">In Progress</option>
//                         <option value="Completed">Completed</option>
//                         <option value="On Hold">On Hold</option>
//                       </Form.Select>
//                     ) : (
//                       <div className="task-field-value">
//                         <span
//                           className={`task-badge ${getStatusBadge(
//                             task.taskStatus
//                           )}`}
//                         >
//                           {task.taskStatus}
//                         </span>
//                       </div>
//                     )}
//                   </div>
//                 </Col>

//                 {/* Priority */}
//                 <Col sm={6}>
//                   <div className="task-field-group">
//                     <label className="task-field-label">Priority</label>
//                     {isEditing ? (
//                       <Form.Select
//                         value={task.taskPriority || ""}
//                         onChange={(e) =>
//                           handleFieldChange("taskPriority", e.target.value)
//                         }
//                         className="task-field-input"
//                       >
//                         <option value="Low">Low</option>
//                         <option value="Medium">Medium</option>
//                         <option value="High">High</option>
//                       </Form.Select>
//                     ) : (
//                       <div className="task-field-value">
//                         <span
//                           className={`task-badge ${getPriorityBadge(
//                             task.taskPriority
//                           )}`}
//                         >
//                           {task.taskPriority}
//                         </span>
//                       </div>
//                     )}
//                   </div>
//                 </Col>
//               </Row>

//               <Row className="g-3">
//                 {/* Assigned To */}
//                 {/* Assigned To */}
//                 <Col sm={6}>
//                   <div className="task-field-group">
//                     <label className="task-field-label">
//                       <i className="fas fa-user me-2"></i>
//                       Assigned To
//                     </label>
//                     {isEditing ? (
//                       <Form.Control
//                         type="text"
//                         value={task.assignedTo || ""}
//                         onChange={(e) =>
//                           handleFieldChange("assignedTo", e.target.value)
//                         }
//                         className="task-field-input"
//                         placeholder="Assign to team member"
//                       />
//                     ) : (
//                       <div
//                         className="task-field-value editable-field"
//                         onClick={() => setIsEditing(true)}
//                         title="Click to edit"
//                         style={{ fontSize: "13px" }}
//                       >
//                         {getUserDisplayName(task.assignedTo) || (
//                           <span className="text-muted">Unassigned</span>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </Col>

//                 {/* Task ID */}
//                 <Col sm={6}>
//                   <div className="task-field-group">
//                     <label className="task-field-label">
//                       <i className="fas fa-hashtag me-2"></i>
//                       Task ID
//                     </label>
//                     <div
//                       className="task-field-value task-id-value"
//                       style={{ fontSize: "13px" }}
//                     >
//                       {task.taskId}
//                     </div>
//                   </div>
//                 </Col>
//               </Row>

//               <Row className="g-3">
//                 {/* Start Date */}
//                 <Col sm={6}>
//                   <div className="task-field-group">
//                     <label className="task-field-label">
//                       <i className="fas fa-play-circle me-2"></i>
//                       Start Date
//                     </label>
//                     {isEditing ? (
//                       <Form.Control
//                         type="date"
//                         value={
//                           task.taskStartDate
//                             ? moment(task.taskStartDate).format("YYYY-MM-DD")
//                             : ""
//                         }
//                         onChange={(e) =>
//                           handleFieldChange("taskStartDate", e.target.value)
//                         }
//                         className="task-field-input"
//                       />
//                     ) : (
//                       <div
//                         className="task-field-value editable-field"
//                         onClick={() => setIsEditing(true)}
//                         title="Click to edit"
//                         style={{ fontSize: "13px" }}
//                       >
//                         {task.taskStartDate ? (
//                           <>
//                             <i className="fas fa-calendar me-1"></i>
//                             {moment(task.taskStartDate).format("MMM DD, YYYY")}
//                           </>
//                         ) : (
//                           <span className="text-muted">Not set</span>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </Col>

//                 {/* End Date */}
//                 <Col sm={6}>
//                   <div className="task-field-group">
//                     <label className="task-field-label">
//                       <i className="fas fa-flag-checkered me-2"></i>
//                       Due Date
//                     </label>
//                     {isEditing ? (
//                       <Form.Control
//                         type="date"
//                         value={
//                           task.taskEndDate
//                             ? moment(task.taskEndDate).format("YYYY-MM-DD")
//                             : ""
//                         }
//                         onChange={(e) =>
//                           handleFieldChange("taskEndDate", e.target.value)
//                         }
//                         className="task-field-input"
//                       />
//                     ) : (
//                       <div
//                         className="task-field-value editable-field"
//                         onClick={() => setIsEditing(true)}
//                         title="Click to edit"
//                         style={{ fontSize: "13px" }}
//                       >
//                         {task.taskEndDate ? (
//                           <>
//                             <i className="fas fa-calendar me-1"></i>
//                             {moment(task.taskEndDate).format("MMM DD, YYYY")}
//                           </>
//                         ) : (
//                           <span className="text-muted">Not set</span>
//                         )}
//                       </div>
//                     )}
//                   </div>
//                 </Col>
//               </Row>

//               {/* Tags */}
//               <div className="task-field-group">
//                 <label className="task-field-label">
//                   <i className="fas fa-tags me-2"></i>
//                   Tags
//                 </label>
//                 {isEditing ? (
//                   <Form.Control
//                     type="text"
//                     value={task.taskTags || ""}
//                     onChange={(e) =>
//                       handleFieldChange("taskTags", e.target.value)
//                     }
//                     className="task-field-input"
//                     placeholder="Comma separated tags (e.g., design, development, urgent)"
//                   />
//                 ) : (
//                   <div
//                     className="task-field-value editable-field"
//                     onClick={() => setIsEditing(true)}
//                     title="Click to edit"
//                   >
//                     {task.taskTags ? (
//                       <div className="task-tags">
//                         {task.taskTags.split(",").map((tag, index) => (
//                           <Badge
//                             key={index}
//                             bg="light"
//                             text="dark"
//                             className="me-1 mb-1"
//                             style={{ fontSize: "13px" }}
//                           >
//                             {/* <i className="fas fa-tag me-1"></i> */}
//                             {tag.trim()}
//                           </Badge>
//                         ))}
//                       </div>
//                     ) : (
//                       <span className="text-muted" style={{ fontSize: "10px" }}>
//                         No tags
//                       </span>
//                     )}
//                   </div>
//                 )}
//               </div>

//               {/* SubTasks Section */}
//               <div className="task-field-group">
//                 <label
//                   className="task-field-label"
//                   style={{ display: "flex", justifyContent: "space-between" }}
//                 >
//                   Sub Tasks ({subTasks.length})
//                   <span>
//                     <Button
//                       variant="primary"
//                       size="sm"
//                       onClick={handleAddSubTask}
//                       className="me-2"
//                     >
//                       Add Sub Task
//                     </Button>
//                   </span>
//                 </label>
//                 <div className="subtasks-list">
//                   {subTasks.length > 0 ? (
//                     subTasks.map((subTask) => (
//                       <div key={subTask.subTaskId} className="subtask-item">
//                         <div className="subtask-header">
//                           <span className="subtask-name">
//                             {subTask.subTaskName}
//                           </span>
//                           <div className="subtask-actions">
//                             <Button
//                               variant="outline-primary"
//                               size="sm"
//                               onClick={() => handleEditSubTask(subTask)}
//                               className="me-1"
//                               title="Edit subtask"
//                               style={{ padding: "3px 10px", margin: "10px" }}
//                             >
//                               Edit
//                             </Button>
//                             <span
//                               className={`subtask-status ${getStatusBadge(
//                                 subTask.subTaskStatus
//                               )}`}
//                             >
//                               {subTask.subTaskStatus}
//                             </span>
//                           </div>
//                         </div>
//                         {subTask.subTaskDescription && (
//                           <div className="subtask-description">
//                             {subTask.subTaskDescription}
//                           </div>
//                         )}
//                         <div className="subtask-meta">
//                           <small className="text-muted">
//                             Due:{" "}
//                             {subTask.subTaskDueDate
//                               ? moment(subTask.subTaskDueDate).format(
//                                   "MMM DD, YYYY"
//                                 )
//                               : "Not set"}
//                           </small>
//                           {subTask.subTaskAssignedTo && (
//                             <small className="text-muted ms-2">
//                               Assigned to:{" "}
//                               {getUserDisplayName(subTask.subTaskAssignedTo)}
//                             </small>
//                           )}
//                         </div>
//                       </div>
//                     ))
//                   ) : (
//                     <div className="text-muted text-center py-2">
//                       No sub tasks yet. Click "Add Sub Task" to create one.
//                     </div>
//                   )}
//                 </div>
//               </div>

//               {/* Comments
//               <div className="task-field-group">
//                 <label className="task-field-label">
//                   <i className="fas fa-comment me-2"></i>
//                   Comments & Notes
//                 </label>
//                 {isEditing ? (
//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     value={task.comments || ""}
//                     onChange={(e) =>
//                       handleFieldChange("comments", e.target.value)
//                     }
//                     className="task-field-input"
//                     placeholder="Add comments or notes about this task..."
//                   />
//                 ) : (
//                   <div
//                     className="task-field-value editable-field task-comments-text"
//                     onClick={() => setIsEditing(true)}
//                     title="Click to edit"
//                   >
//                     {task.comments || (
//                       <span className="text-muted">No comments added</span>
//                     )}
//                   </div>
//                 )}
//               </div> */}

//               {/* Created Info */}
//               <div className="task-field-group">
//                 <div className="task-meta-info">
//                   <small className="text-muted">
//                     <i className="fas fa-clock me-1"></i>
//                     Created{" "}
//                     {task.createdDate
//                       ? moment(task.createdDate).format(
//                           "MMM DD, YYYY [at] h:mm A"
//                         )
//                       : "N/A"}
//                     {task.createdBy && ` by ${task.createdBy}`}
//                   </small>
//                 </div>
//               </div>
//             </div>
//           ) : (
//             <div className="text-center py-4 text-muted">
//               <i className="fas fa-exclamation-triangle fa-2x mb-3"></i>
//               <p>Task not found or unable to load task details.</p>
//               <Button variant="outline-secondary" onClick={handleClose}>
//                 Close
//               </Button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* SubTask Modal */}
//       <Modal
//         show={showSubTaskModal}
//         onHide={() => setShowSubTaskModal(false)}
//         size="lg"
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>
//             <i className="fas fa-plus-circle me-2"></i>
//             Add New Sub Task
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Row className="g-3">
//               <Col md={12}>
//                 <Form.Group>
//                   <Form.Label>SubTask Name *</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={subTaskForm.SubTaskName}
//                     onChange={(e) =>
//                       handleSubTaskFormChange("SubTaskName", e.target.value)
//                     }
//                     placeholder="Enter subtask name"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={12}>
//                 <Form.Group>
//                   <Form.Label>Description</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     value={subTaskForm.SubTaskDescription}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskDescription",
//                         e.target.value
//                       )
//                     }
//                     placeholder="Enter subtask description"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Assigned To</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={subTaskForm.SubTaskAssignedTo}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskAssignedTo",
//                         e.target.value
//                       )
//                     }
//                     placeholder="Assign to team member"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Status</Form.Label>
//                   <Form.Select
//                     value={subTaskForm.SubTaskStatus}
//                     onChange={(e) =>
//                       handleSubTaskFormChange("SubTaskStatus", e.target.value)
//                     }
//                   >
//                     <option value="Pending">Pending</option>
//                     <option value="In Progress">In Progress</option>
//                     <option value="Completed">Completed</option>
//                     <option value="On Hold">On Hold</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Due Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={subTaskForm.SubTaskDueDate}
//                     onChange={(e) =>
//                       handleSubTaskFormChange("SubTaskDueDate", e.target.value)
//                     }
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Actual Start Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={subTaskForm.SubTaskActualStateDate}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskActualStateDate",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={12}>
//                 <Form.Group>
//                   <Form.Label>Actual End Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={subTaskForm.SubTaskActualEndDate}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskActualEndDate",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowSubTaskModal(false)}
//             disabled={subTaskLoading}
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleSubTaskSubmit}
//             disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
//           >
//             {subTaskLoading ? (
//               <>
//                 <span
//                   className="spinner-border spinner-border-sm me-2"
//                   role="status"
//                   aria-hidden="true"
//                 ></span>
//                 Creating...
//               </>
//             ) : (
//               <>
//                 <i className="fas fa-save me-1"></i>
//                 Create Sub Task
//               </>
//             )}
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Edit SubTask Modal */}
//       <Modal
//         show={showEditSubTaskModal}
//         onHide={() => {
//           setShowEditSubTaskModal(false);
//           setEditingSubTask(null);
//         }}
//         size="lg"
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>
//             <i className="fas fa-edit me-2"></i>
//             Edit Sub Task
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Row className="g-3">
//               <Col md={12}>
//                 <Form.Group>
//                   <Form.Label>SubTask Name *</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={subTaskForm.SubTaskName}
//                     onChange={(e) =>
//                       handleSubTaskFormChange("SubTaskName", e.target.value)
//                     }
//                     placeholder="Enter subtask name"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={12}>
//                 <Form.Group>
//                   <Form.Label>Description</Form.Label>
//                   <Form.Control
//                     as="textarea"
//                     rows={3}
//                     value={subTaskForm.SubTaskDescription}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskDescription",
//                         e.target.value
//                       )
//                     }
//                     placeholder="Enter subtask description"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Assigned To</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={subTaskForm.SubTaskAssignedTo}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskAssignedTo",
//                         e.target.value
//                       )
//                     }
//                     placeholder="Assign to team member"
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Status</Form.Label>
//                   <Form.Select
//                     value={subTaskForm.SubTaskStatus}
//                     onChange={(e) =>
//                       handleSubTaskFormChange("SubTaskStatus", e.target.value)
//                     }
//                   >
//                     <option value="Pending">Pending</option>
//                     <option value="In Progress">In Progress</option>
//                     <option value="Completed">Completed</option>
//                     <option value="On Hold">On Hold</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Due Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={subTaskForm.SubTaskDueDate}
//                     onChange={(e) =>
//                       handleSubTaskFormChange("SubTaskDueDate", e.target.value)
//                     }
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={6}>
//                 <Form.Group>
//                   <Form.Label>Actual Start Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={subTaskForm.SubTaskActualStateDate}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskActualStateDate",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </Form.Group>
//               </Col>

//               <Col md={12}>
//                 <Form.Group>
//                   <Form.Label>Actual End Date</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={subTaskForm.SubTaskActualEndDate}
//                     onChange={(e) =>
//                       handleSubTaskFormChange(
//                         "SubTaskActualEndDate",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => {
//               setShowEditSubTaskModal(false);
//               setEditingSubTask(null);
//             }}
//             disabled={subTaskLoading}
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleUpdateSubTask}
//             disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
//           >
//             {subTaskLoading ? (
//               <>
//                 <span
//                   className="spinner-border spinner-border-sm me-2"
//                   role="status"
//                   aria-hidden="true"
//                 ></span>
//                 Updating...
//               </>
//             ) : (
//               <>
//                 <i className="fas fa-save me-1"></i>
//                 Update Sub Task
//               </>
//             )}
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// };

// export default TaskViewSidebar;
